import styles from '../../assets/index.css?inline';
import {
  createSignal,
  onMount,
  Show,
  splitProps,
  onCleanup,
  createEffect,
  lazy,
  ErrorBoundary,
  createMemo,
} from 'solid-js';
import { EventTracker, eventTrackerStore } from '@/utils/event-tracker-store';
import { CommandData, isDefined, isNotDefined } from '@/utils/common';
import { Search as SearchWidget } from '@/components/SearchWidget';
import { SearchProps } from '@/types/search';
import { PopupParams } from './types';
import { getEventTracker } from '@/utils/trackEvent';
import {
  EventType,
  WIDGET_SOURCE,
  WIDGET_TYPE,
  WidgetEvents,
} from '@/types/constants';
import { Logger } from '@/utils/logger';

import { injectBrainfishStyles } from '@/utils/inject-brainfish-styles';

const SlideOverPanel = lazy(() => import('@/components/Slider/Slider'));
import { AnimationDurations } from '@/components/Slider/Slider';
import { dispatchCustomEvent } from '@/utils/emitCustomEvent';
import debounce from '@/utils/debounce';
import { WidgetMode } from '@/constants';

export type PopupProps = SearchProps &
  PopupParams & {
    widgetMode?: WidgetMode;
    defaultOpen?: boolean;
    isOpen?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onToggle?: (value: boolean) => void;
  };

export const Popup = (props: PopupProps) => {
  const widgetType = WIDGET_TYPE.HELP;
  const widgetSource = props.widgetSource || WIDGET_SOURCE.WEB;
  const widgetMode = props.widgetMode || WidgetMode.SLIDE_OVER;
  const [popupProps, widgetProps] = splitProps(props, [
    'onOpen',
    'onClose',
    'autoShowDelay',
    'theme',
    'isOpen',
    'defaultOpen',
  ]);

  const [eventTracker, setEventTracker] = eventTrackerStore;
  const [hostElementCss, setHostElementCss] = createSignal('');
  const [isWidgetOpened, setIsWidgetOpened] = createSignal(
    // eslint-disable-next-line solid/reactivity
    popupProps.isOpen ?? false,
  );

  const [isSearchWidgetVisibleInternal, setIsSearchWidgetVisibleInternal] =
    createSignal(isWidgetOpened());

  const isSearchWidgetVisible = createMemo(() =>
    isSearchWidgetVisibleInternal(),
  );

  const [settings, setSettings] = createSignal(props.settings || {});
  const [apiHost, setApiHost] = createSignal(
    props.apiHost || 'https://app.brainfi.sh',
  );
  const [mounted, setMounted] = createSignal(false);
  const [answering, setAnswering] = createSignal(false);
  const [triggerElement, setTriggerElement] =
    createSignal<HTMLElement | null>(); // element that triggered the widget to open
  const debouncedToggle = debounce(
    () => props.onToggle!(isWidgetOpened()),
    AnimationDurations.DEFAULT,
  );

  onMount(() => {
    setMounted(true);
    if (popupProps.defaultOpen) openWidget();
    window.addEventListener('message', processIncomingEvent);
    const autoShowDelay = popupProps.autoShowDelay;
    if (isDefined(autoShowDelay)) {
      setTimeout(() => {
        openWidget();
      }, autoShowDelay);
    }
  });

  onCleanup(() => {
    window.removeEventListener('message', processIncomingEvent);
  });

  const initializeTracker = async (disableTracking: boolean) => {
    // using setTimeout hack to make sure props.apiHost is settled before tracking.
    // have noticed that props.apiHost is not available in some cases, but don't want to use default value for local testing.
    const apiHost = props.apiHost || 'https://app.brainfi.sh';
    setApiHost(apiHost);

    const eventTracker: EventTracker = await getEventTracker({
      apiHost,
      apiKey: props.apiKey,
      source: widgetType,
      skip: disableTracking,
    });

    // Set the eventTracker in the store
    setEventTracker((prev) => ({
      ...prev,
      ...eventTracker, // eventTracker contains the new properties
    }));

    // Log widget loaded event
    eventTracker.track({
      name: 'Widget loaded',
      type: EventType.WidgetLoaded,
      data: {},
    });
  };

  // set up the tracker
  createEffect(async () => {
    const { disableTracking } = props;

    if (!mounted() || disableTracking || !props.apiKey) {
      return;
    }

    try {
      await initializeTracker(!!disableTracking);
    } catch (error) {
      Logger.warn('Error initializing tracker', error);
    }
  });

  createEffect(() => {
    if (props.theme) {
      setHostElementCss(
        injectBrainfishStyles(props.theme, {
          position: 'fixed',
          zIndex: 99999999,
        }),
      ); // inject this into the host element
    }

    setSettings(props.settings || {});

    if (isNotDefined(props.isOpen) || props.isOpen === isWidgetOpened()) return;
    toggleWidget();
  });

  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const processIncomingEvent = (event: MessageEvent<CommandData>) => {
    const { data } = event;
    if (!data.isFromBrainfish) return;

    if (!triggerElement() && data.trigger) {
      setTriggerElement(
        document.querySelector(`.${data.trigger}`) as HTMLElement,
      );
    }
    if (data.command === 'openHelpDrawer') openWidget();
    if (data.command === 'closeHelpDrawer') closeWidget();
    if (data.command === 'toggleHelpDrawer') toggleWidget();
  };

  const openWidget = () => {
    if (isWidgetOpened()) return;

    setIsWidgetOpened(true);
    setIsSearchWidgetVisibleInternal(true);
    const trackName = 'Open widget panel';
    const type = EventType.OpenWidget;
    eventTracker.track({ name: trackName, type, data: {} });

    // delay a second to allow the animation to finish
    setTimeout(() => {
      popupProps.onOpen?.();
      dispatchCustomEvent(WidgetEvents.OnOpened, {
        message: 'Widget opened',
      });
    }, AnimationDurations.DEFAULT);

    if (!props.disableModalBackdrop) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('pointerdown', closeWidget);
    }
  };

  const closeWidget = async () => {
    if (!isWidgetOpened()) return;

    const trackName = 'Close widget panel';
    const type = EventType.CloseWidget;
    eventTracker.track({ name: trackName, type, data: {} });

    if (!props.disableModalBackdrop) {
      document.body.style.overflow = 'auto';
      document.removeEventListener('pointerdown', closeWidget);
    }

    // set focus back to the element that triggered the widget to open
    triggerElement()?.focus();
    setTriggerElement(null);

    if (widgetMode === WidgetMode.SLIDE_OVER) {
      setIsWidgetOpened(false);

      // delay a second to allow the animation to finish
      setTimeout(() => {
        // emit custom `BrainfishHelpWidgetClosed` event
        dispatchCustomEvent(WidgetEvents.OnClosed, {
          message: 'Widget closed',
        });
        setIsSearchWidgetVisibleInternal(false);
      }, AnimationDurations.DEFAULT + 100);
    } else {
      setIsWidgetOpened(false);
    }
  };

  const toggleWidget = () => {
    isWidgetOpened() ? closeWidget() : openWidget();
  };

  const handleSlideOverPanelToggle = (evt: boolean) => {
    // somehow, this function triggered by onToggle is only ever called when closing the slideover panel
    if (evt === false) {
      closeWidget();
    }
    if (props.onToggle) {
      debouncedToggle();
    }
  };

  return (
    <ErrorBoundary fallback={(err) => err}>
      <style>
        {styles}
        {hostElementCss()}
      </style>
      {widgetMode === WidgetMode.POPUP && (
        <Show when={isWidgetOpened()}>
          <div
            class="relative z-[999999]"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <style>{styles}</style>
            <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity animate-fade-in z-1" />
            <div class="fixed inset-0 z-10 overflow-y-auto">
              <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div
                  class="relative p-4 transform rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
                  style={{
                    'background-color': props.theme?.backgroundColor ?? '#fff',
                  }}
                  on:pointerdown={stopPropagation}
                >
                  <SearchWidget {...widgetProps} theme={props.theme} />
                </div>
              </div>
            </div>
          </div>
        </Show>
      )}
      {widgetMode === WidgetMode.SLIDE_OVER && (
        <SlideOverPanel
          panelTitle="Help"
          isOpen={isWidgetOpened()}
          onClose={closeWidget}
          onToggle={handleSlideOverPanelToggle}
          apiHost={apiHost()}
          apiKey={props.apiKey}
          answering={answering()}
          disableModalBackdrop={props.disableModalBackdrop}
          {...settings()}
        >
          <Show when={isSearchWidgetVisible()}>
            <SearchWidget
              {...widgetProps}
              widgetSource={widgetSource}
              theme={props.theme}
              onAnswer={setAnswering}
            />
          </Show>
        </SlideOverPanel>
      )}
    </ErrorBoundary>
  );
};
